import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { useAuth } from '../../../Hooks/useAuth';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import { MODULE_NAMES } from '../../../Constants/modules';
import {
  getShiftAllowanceBaseURL,
  updateShiftAllowanceApproval,
} from '../../../Api/request/shiftAllowance';
import './ShiftAllowanceApprovalHistory.scss';

const PAGE_SIZE = 10;
// Keep the table package's pagination defaults stable across profile updates.
const PAGINATION_CONFIG = { page: 1, limit: PAGE_SIZE, rowsPerPageOptions: [] };

const buildFilterValue = (value = '') => {
  const normalizedValue = `${value || ''}`.trim();
  return normalizedValue ? { label: normalizedValue, value: normalizedValue } : null;
};

const buildNotificationFilter = (field, value) => (value ? {
  field,
  selectedValues: [value],
  type: 'filter',
} : null);

const useNotificationFilters = () => useMemo(() => {
  const params = new URLSearchParams(window.location.search);
  const requestId = buildFilterValue(params.get('allowanceRequestId') || params.get('requestId'));
  const requestStatus = buildFilterValue(params.get('requestStatus'));

  return [
    buildNotificationFilter('requestId', requestId),
    buildNotificationFilter('requestStatus', requestStatus),
  ].filter(Boolean);
}, []);

const normalizePermissionAction = (action = '') => action.toString().trim()
  .toLowerCase();

const getPermissionActions = (permissions = {}, moduleName = '') => {
  const moduleCandidates = [
    moduleName,
    moduleName.replace(/-/g, '_'),
    moduleName.replace(/_/g, '-'),
  ];

  for (const candidate of moduleCandidates) {
    if (Array.isArray(permissions?.[candidate])) {
      return permissions[candidate].map(normalizePermissionAction);
    }
  }

  return [];
};

const hasPermissionAction = (permissions = {}, moduleName = '', action = '') => (
  getPermissionActions(permissions, moduleName).includes(normalizePermissionAction(action))
);

const APPROVAL_HISTORY_EXPORT_COLUMNS = [
  { field: 'requestId', header: 'Allowance Request Id' },
  { field: 'initiatorId', header: 'Initiator ID' },
  { field: 'initiator', header: 'Initiator Name' },
  { field: 'monthYear', header: 'Month - Year' },
  { field: 'requestStatusText', header: 'Request Status' },
  { field: 'requesterId', header: 'Requester ID' },
  { field: 'requesterName', header: 'Requester Name' },
  { field: 'requesterDate', header: 'Requested Date' },
  { field: 'l1Status', header: 'L1 Approval Status' },
  { field: 'l1Id', header: 'L1 Approver ID' },
  { field: 'l1Name', header: 'L1 Approver Name' },
  { field: 'l1Date', header: 'L1 Approval Date' },
  { field: 'l1Comments', header: 'L1 Comments' },
  { field: 'l2Status', header: 'L2 Approval Status' },
  { field: 'l2Id', header: 'L2 Approver ID' },
  { field: 'l2Name', header: 'L2 Approver Name' },
  { field: 'l2Date', header: 'L2 Approval Date' },
  { field: 'l2Comments', header: 'L2 Comments' },
];

const getStatusClassName = (status = '') => (
  `shift-allowance-approval-history__status shift-allowance-approval-history__status--${status
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}`
);

const getExportText = (value) => {
  if (React.isValidElement(value)) return '';
  return value ?? '';
};

const getExportName = (value) => getEmployeeNameOnly(getExportText(value));

const getExportDate = (value) => String(getExportText(value)).split(',')[0].trim();

const getEmployeeId = (value = '') => {
  const match = String(value || '')
    .trim()
    .match(/^(S\d+)\s*-\s*/i);
  return match ? match[1] : '';
};

const normalizeHistoryFilterField = (field = '') => {
  const normalizedField = `${field || ''}`.trim();
  const compactField = normalizedField.toLowerCase().replace(/[^a-z0-9]/g, '');

  if (['allowancerequestid', 'allowancerequestnumber', 'requestid', 'requestnumber'].includes(compactField)) {
    return 'requestId';
  }

  return normalizedField;
};

const normalizeApprovalHistoryPayload = (payload = {}) => ({
  ...payload,
  filter: Array.isArray(payload.filter)
    ? payload.filter.map((filter) => ({
      ...filter,
      field: normalizeHistoryFilterField(filter?.field),
    }))
    : payload.filter,
});

const getApprovalHistoryExportRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.tableData)) return payload.tableData;
  if (Array.isArray(payload?.data?.tableData)) return payload.data.tableData;
  return [];
};

const buildApprovalHistoryExportPayload = (payload = []) => {
  const rows = getApprovalHistoryExportRows(payload);

  return {
    tableConfiguration: APPROVAL_HISTORY_EXPORT_COLUMNS.map((column, index) => ({
      ...column,
      key: column.field,
      visible: true,
      order: index + 1,
    })),
    tableData: rows.map((row) => ({
      requestId: getExportText(row.requestId),
      initiatorId: getEmployeeId(getExportText(row.initiator)),
      initiator: getExportName(row.initiator),
      monthYear: getExportText(row.monthYear),
      requestStatusText: getExportText(row.requestStatus),
      requesterId: getEmployeeId(getExportText(row.requesterName)),
      requesterName: getExportName(row.requesterName),
      requesterDate: getExportDate(row.requesterDate),
      l1Status: getExportText(row.l1Status),
      l1Id: getEmployeeId(getExportText(row.l1Name)),
      l1Name: getExportName(row.l1Name),
      l1Date: getExportDate(row.l1Date),
      l1Comments: getExportText(row.l1Comments),
      l2Status: getExportText(row.l2Status),
      l2Id: getEmployeeId(getExportText(row.l2Name)),
      l2Name: getExportName(row.l2Name),
      l2Date: getExportDate(row.l2Date),
      l2Comments: getExportText(row.l2Comments),
    })),
  };
};

const getEmployeeNameOnly = (value = '') => String(value || '').replace(/^S\d+\s*-\s*/i, '')
  .trim();

const StatusBadge = ({ clickable, status }) => (
  status ? (
    <span
      className={[
        getStatusClassName(status),
        clickable ? 'shift-allowance-approval-history__status--clickable' : '',
      ].filter(Boolean).join(' ')}
    >
      {status}
    </span>
  ) : null
);

StatusBadge.propTypes = {
  clickable: PropTypes.bool,
  status: PropTypes.string,
};

StatusBadge.defaultProps = {
  clickable: false,
  status: '',
};

const DetailCell = ({
  date,
  level,
  name,
  nameFirst,
  note,
  onStatusClick,
  row,
  status,
}) => {
  const isOpenStatus = String(status).toLowerCase() === 'open';
  const isL2Blocked = level === 'L2' && String(row?.l1Status || '').toLowerCase() !== 'approved';
  const isClickable = isOpenStatus && !isL2Blocked;
  const handleClick = () => {
    if (isClickable) onStatusClick(row, level);
  };

  const handleKeyDown = (event) => {
    if (!isClickable || !['Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    handleClick();
  };

  return (
    <div
      className={[
        'shift-allowance-approval-history__detail',
        isClickable ? 'shift-allowance-approval-history__detail--clickable' : '',
      ].filter(Boolean).join(' ')}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {status && (
        <StatusBadge
          clickable={isClickable}
          status={status}
        />
      )}
      {nameFirst && name && <span className="shift-allowance-approval-history__name">{name}</span>}
      {date && <span className="shift-allowance-approval-history__date">{date}</span>}
      {!nameFirst && name && <span className="shift-allowance-approval-history__name">{name}</span>}
      {note && (
        <span className="shift-allowance-approval-history__note" title={note}>
          {note}
        </span>
      )}
    </div>
  );
};

DetailCell.propTypes = {
  date: PropTypes.string,
  level: PropTypes.string,
  name: PropTypes.string,
  nameFirst: PropTypes.bool,
  note: PropTypes.string,
  onStatusClick: PropTypes.func,
  row: PropTypes.object,
  status: PropTypes.string,
};

DetailCell.defaultProps = {
  date: '',
  level: '',
  name: '',
  nameFirst: false,
  note: '',
  onStatusClick: () => {},
  row: null,
  status: '',
};

const ApprovalStatusDialog = ({
  dialog,
  onClose,
  onFieldChange,
  onSubmit,
}) => (
  <div
    className="shift-allowance-approval-history__status-backdrop"
    onClick={(event) => {
      if (!dialog.submitting && event.target === event.currentTarget) onClose();
    }}
    role="presentation"
  >
    <form className="shift-allowance-approval-history__status-modal" onSubmit={onSubmit}>
      <div className="shift-allowance-approval-history__status-modal-header">
        {`Update status - ${getEmployeeNameOnly(dialog.row?.initiator)}`}
      </div>
      <div className="shift-allowance-approval-history__status-modal-body">
        <label
          className="shift-allowance-approval-history__status-modal-label"
          htmlFor="shift-allowance-approval-history-status"
        >
          Status*
        </label>
        <div className="shift-allowance-approval-history__status-modal-select-wrap">
          <select
            className="shift-allowance-approval-history__status-modal-select"
            disabled={dialog.submitting}
            id="shift-allowance-approval-history-status"
            onChange={(event) => onFieldChange('status', event.target.value)}
            value={dialog.status}
          >
            <option disabled value="">Select status</option>
            <option value="approve">Approve</option>
            <option value="reject">Reject</option>
          </select>
        </div>

        <label
          className="shift-allowance-approval-history__status-modal-label"
          htmlFor="shift-allowance-approval-history-comment"
        >
          Comments*
        </label>
        <textarea
          className="shift-allowance-approval-history__status-modal-input"
          disabled={dialog.submitting}
          id="shift-allowance-approval-history-comment"
          onChange={(event) => onFieldChange('comments', event.target.value)}
          placeholder="Enter..."
          rows={4}
          value={dialog.comments}
        />
      </div>

      <div className="shift-allowance-approval-history__status-modal-footer">
        <button
          className={[
            'shift-allowance-approval-history__status-modal-btn',
            'shift-allowance-approval-history__status-modal-btn-ghost',
          ].join(' ')}
          disabled={dialog.submitting}
          onClick={onClose}
          type="button"
        >
          Cancel
        </button>
        <button
          className={[
            'shift-allowance-approval-history__status-modal-btn',
            'shift-allowance-approval-history__status-modal-btn-primary',
          ].join(' ')}
          disabled={dialog.submitting}
          type="submit"
        >
          {dialog.submitting ? 'Updating...' : 'Update'}
        </button>
      </div>
    </form>
  </div>
);

ApprovalStatusDialog.propTypes = {
  dialog: PropTypes.shape({
    comments: PropTypes.string,
    row: PropTypes.object,
    status: PropTypes.string,
    submitting: PropTypes.bool,
    visible: PropTypes.bool,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const useHistoryNotificationFilters = () => {
  const initialNotifFilters = useNotificationFilters();
  const notificationFiltersActive = useRef(true);
  const handleFilterUpdate = useCallback(() => {
    notificationFiltersActive.current = false;
    const url = new URL(window.location.href);
    ['allowanceRequestId', 'requestId', 'requestStatus', 'fromNotification'].forEach((key) => {
      url.searchParams.delete(key);
    });
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);
  const transformHistoryPayload = useCallback((payload) => {
    const normalizedPayload = normalizeApprovalHistoryPayload(payload);
    if (!notificationFiltersActive.current || !initialNotifFilters.length) return normalizedPayload;

    // Notification restrictions apply until the user takes control of the filters.
    const notificationFields = new Set(initialNotifFilters.map((filter) => filter.field));
    return {
      ...normalizedPayload,
      filter: [
        ...(normalizedPayload.filter || []).filter((filter) => !notificationFields.has(filter.field)),
        ...initialNotifFilters,
      ],
    };
  }, [initialNotifFilters]);
  return { handleFilterUpdate, transformHistoryPayload };
};

const ShiftAllowanceApprovalHistory = () => {
  const { userProfile } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [tableScrollHeight, setTableScrollHeight] = useState('420px');
  const [statusDialog, setStatusDialog] = useState({
    comments: '',
    level: '',
    row: null,
    status: '',
    submitting: false,
    visible: false,
  });
  const baseURL = `${getShiftAllowanceBaseURL()}/history`;
  const { handleFilterUpdate, transformHistoryPayload } = useHistoryNotificationFilters();
  const tableKey = `shift-allowance-approval-history-${userProfile?.access_token || 'anonymous'}-${refreshKey}`;

  const userInfo = useMemo(() => ({
    emailId: userProfile?.email || '',
    permissions: userProfile?.permissions || {},
    roleDetails: userProfile?.roleDetails || [],
    employeeId: userProfile?.employeeId || '',
    globalColors: userProfile?.siteConfiguration?.global_colors || {},
  }), [userProfile]);

  const canUpdateApproval = useMemo(() => hasPermissionAction(
    userProfile?.permissions || {},
    MODULE_NAMES.RESOURCE_SHIFT_ALLOWANCE_APPROVAL_HISTORY,
    'update',
  ), [userProfile?.permissions]);

  const openStatusDialog = useCallback((row, level) => {
    if (!canUpdateApproval) {
      toast.error('You do not have permission to update allowance approval status.');
      return;
    }

    setStatusDialog({
      comments: '',
      level,
      row,
      status: '',
      submitting: false,
      visible: true,
    });
  }, [canUpdateApproval]);

  const closeStatusDialog = useCallback(() => {
    setStatusDialog({
      comments: '',
      level: '',
      row: null,
      status: '',
      submitting: false,
      visible: false,
    });
  }, []);

  const handleDialogFieldChange = useCallback((field, value) => {
    setStatusDialog((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleStatusSave = useCallback(async (event) => {
    event?.preventDefault();

    if (!canUpdateApproval) {
      toast.error('You do not have permission to update allowance approval status.');
      return;
    }

    if (!statusDialog.status) {
      toast.error('Please select status.');
      return;
    }

    if (!statusDialog.comments.trim()) {
      toast.error('Please enter comment.');
      return;
    }

    const requestId = statusDialog.row?.requestId || statusDialog.row?.id;
    if (!requestId) {
      toast.error('Allowance request id is missing.');
      return;
    }

    setStatusDialog((current) => ({ ...current, submitting: true }));

    try {
      const response = await updateShiftAllowanceApproval(requestId, {
        action: statusDialog.status,
        comments: statusDialog.comments.trim(),
        level: statusDialog.level,
      });

      if (response?.data?.success) {
        toast.success(response.data.message || 'Shift allowance approval updated successfully.');
        handleFilterUpdate();
        closeStatusDialog();
        setRefreshKey((current) => current + 1);
        return;
      }

      toast.error(response?.data?.message || 'Unable to update approval status.');
    } catch (error) {
      toast.error(
        error?.response?.data?.message
        || error?.message
        || 'Unable to update approval status.',
      );
    } finally {
      setStatusDialog((current) => ({ ...current, submitting: false }));
    }
  }, [canUpdateApproval, closeStatusDialog, handleFilterUpdate, statusDialog]);

  const handleFilteredApiResponse = useCallback((response) => {
    normalizeTableUtilityResponse(response);
    const tableData = response?.data?.tableData;

    if (Array.isArray(tableData)) {
      const displayRows = tableData.map((row) => ({
        ...row,
        requestStatus: <StatusBadge status={row.requestStatus} />,
        requesterDetails: (
          <DetailCell
            date={row.requesterDate}
            name={row.requesterName}
            nameFirst
          />
        ),
        l1ApprovalDetails: (
          <DetailCell
            date={row.l1Date}
            level="L1"
            name={row.l1Name}
            note={row.l1Comments}
            onStatusClick={canUpdateApproval ? openStatusDialog : undefined}
            row={row}
            status={row.l1Status}
          />
        ),
        l2ApprovalDetails: (
          <DetailCell
            date={row.l2Date}
            level="L2"
            name={row.l2Name}
            note={row.l2Comments}
            onStatusClick={canUpdateApproval ? openStatusDialog : undefined}
            row={row}
            status={row.l2Status}
          />
        ),
      }));

      response.data.tableData = displayRows;
      if (response.data.data && typeof response.data.data === 'object') {
        response.data.data.tableData = displayRows;
      }
      response.tableData = displayRows;
    }

    return response;
  }, [canUpdateApproval, openStatusDialog]);

  useEffect(() => {
    const handleResize = () => {
      setTableScrollHeight(`${Math.max(Math.floor(window.innerHeight - 285), 260)}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      exportHeaderUnmerge: true,
      baseURL,
      showToolBar: true,
      showPagination: true,
      showTieredMenu: true,
      headers: { token: userProfile?.access_token || '' },
      userInfo,
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => 'Shift_Allowance_Approval_History',
          sheetName: () => 'Approval History',
          transformResponse: buildApprovalHistoryExportPayload,
        },
      },
      handler: {
        handleFilteredApiResponse,
        onFilterUpdate: handleFilterUpdate,
        transformPayload: transformHistoryPayload,
      },
      toolBarSettings: {
        showFilterOption: true,
        showColumnFilterMenu: true,
        showAddNew: false,
        showExpandCollapse: false,
        showDownloadExcel: true,
        downloadExcelSheetName: 'Approval History',
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showToolBarHeading: true,
        toolBarHeading: 'Allowance approval history',
        showReset: false,
        showFilters: true,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: PAGE_SIZE,
      tableScrollHeight,
      loaderSkeleton: { rows: PAGE_SIZE, bodyCells: 2, columns: 7 },
      paginationConfig: PAGINATION_CONFIG,
      showExpandCollapse: false,
      maxFrozenColumns: { desktop: 2, tablet: 2, mobile: 2 },
    },
  }), [
    baseURL, handleFilteredApiResponse, handleFilterUpdate, tableScrollHeight,
    transformHistoryPayload, userInfo, userProfile?.access_token,
  ]);

  return (
    <div className="shift-allowance-approval-history">
      <TableUtility key={tableKey} configuration={configuration} showFilterOption />
      {statusDialog.visible ? (
        <ApprovalStatusDialog
          dialog={statusDialog}
          onClose={closeStatusDialog}
          onFieldChange={handleDialogFieldChange}
          onSubmit={handleStatusSave}
        />
      ) : null}
    </div>
  );
};

export default ShiftAllowanceApprovalHistory;
